import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ArrowBendUpLeft, Paperclip } from "@phosphor-icons/react";
import {
  boardAppealAttachmentUrl,
  fetchBoardAppealEmailAPI,
  searchFirmPeopleAPI,
} from "../../../service/boardAppeals";
import {
  AddressWrap,
  AttachmentList,
  Banner,
  Chip,
  LinkButton,
  MailAttachments,
  MailBody,
  MailHeader,
  MailLinks,
  MailMeta,
  MailOpen,
  MailPreview,
  MailRow,
  MailScroll,
  MailSubject,
  MailTitle,
  MailWhen,
  MailWho,
  Muted,
  NoteBox,
  PanelLabel,
  ReplyActions,
  ReplyBox,
  ReplyButton,
  ReplyField,
  RowTop,
  SignUpButton,
  SmallPrint,
  Suggestion,
  Suggestions,
  TurnDownButton,
} from "../BoardAppealsStyled";
import { BOARD_DECISION_MATCH, formatSentAt, personLabel } from "../utils";

const isSent = (item) => /^sent/i.test(item.folder || "");

const who = (item) => {
  if (isSent(item)) {
    const first = (item.to || [])[0];
    const others = Math.max(0, (item.to || []).length - 1);
    return `To ${personLabel(first) || "(no recipient)"}${others ? ` +${others}` : ""}`;
  }
  return personLabel(item.from, item.fromName) || "(unknown sender)";
};

const splitAddresses = (text) =>
  String(text || "")
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

// The viewer can't show an email inside an email, so those download instead.
const isEmailFile = (name) => /\.(eml|msg)$/i.test(name || "");

const lastToken = (value) => value.split(/[,;]/).pop().trim();

/**
 * A To or Cc box that offers firm people once two letters of a name or
 * address are typed. Outside addresses are typed as before.
 */
const AddressField = ({ id, value, onChange }) => {
  const [options, setOptions] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const query = lastToken(value);

  useEffect(() => {
    if (!open || query.length < 2) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchFirmPeopleAPI(query)
        .then(({ data }) => {
          if (cancelled) return;
          const typed = new Set(splitAddresses(value).map((a) => a.toLowerCase()));
          setOptions((data?.people || []).filter((p) => !typed.has(p.email)));
          setHighlight(0);
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, value]);

  const choose = (person) => {
    const parts = value.split(/[,;]/);
    parts[parts.length - 1] = person.email;
    onChange(
      `${parts
        .map((p) => p.trim())
        .filter(Boolean)
        .join(", ")}, `
    );
    setOptions([]);
  };

  const onKeyDown = (event) => {
    if (!options.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((h) => (h + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((h) => (h - 1 + options.length) % options.length);
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      choose(options[highlight]);
    } else if (event.key === "Escape") {
      setOptions([]);
    }
  };

  const showing = open && options.length > 0;
  return (
    <AddressWrap>
      <ReplyField
        id={id}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-expanded={showing}
        aria-controls={`${id}-options`}
        aria-autocomplete="list"
        onChange={(e) => {
          setOpen(true);
          onChange(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      />
      {showing ? (
        <Suggestions id={`${id}-options`} role="listbox">
          {options.map((person, i) => (
            <Suggestion
              key={person.email}
              role="option"
              aria-selected={i === highlight}
              $active={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(person);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <strong>{person.name || person.email}</strong>
              <span>{person.email}</span>
            </Suggestion>
          ))}
        </Suggestions>
      ) : null}
    </AddressWrap>
  );
};

AddressField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ReplyComposer = ({ item, onSend, onCancel }) => {
  const [to, setTo] = useState((item.replyTo || []).join(", "));
  const [cc, setCc] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    if (!body.trim()) {
      setError("Write the reply first.");
      return;
    }
    if (!splitAddresses(to).length) {
      setError("Say who the reply goes to.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await onSend(item, { body, to: splitAddresses(to), cc: splitAddresses(cc) });
    } catch (err) {
      setError(err?.message || "The reply was not sent.");
      setSending(false);
    }
  };

  return (
    <ReplyBox aria-label="Reply">
      <PanelLabel htmlFor={`reply-to-${item.id}`}>To</PanelLabel>
      <AddressField id={`reply-to-${item.id}`} value={to} onChange={setTo} />
      <PanelLabel htmlFor={`reply-cc-${item.id}`}>Cc</PanelLabel>
      <AddressField id={`reply-cc-${item.id}`} value={cc} onChange={setCc} />
      <NoteBox
        aria-label="Reply text"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        $invalid={Boolean(error) && !body.trim()}
        autoFocus
      />
      {error ? <Banner role="alert" style={{ margin: 0 }}>{error}</Banner> : null}
      <SmallPrint>Sends from your Outlook, in this email&apos;s thread.</SmallPrint>
      <ReplyActions>
        <SignUpButton type="button" onClick={send} disabled={sending}>
          {sending ? "Sending…" : "Send"}
        </SignUpButton>
        <TurnDownButton type="button" onClick={onCancel} disabled={sending}>
          Cancel
        </TurnDownButton>
      </ReplyActions>
    </ReplyBox>
  );
};

ReplyComposer.propTypes = {
  item: PropTypes.object.isRequired,
  onSend: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const joined = (list) => (list || []).filter(Boolean).join(", ");

const OpenEmail = ({ reviewId, item, email, onOpen, onOpenAttachment, children }) => {
  const [showEarlier, setShowEarlier] = useState(false);

  if (email.loading) return <MailOpen><Muted style={{ margin: 0 }}>Opening the email…</Muted></MailOpen>;

  const data = email.data;
  return (
    <MailOpen aria-label="Full email">
      {email.error ? (
        <Banner role="alert" style={{ margin: 0 }}>{email.error}</Banner>
      ) : null}
      {data ? (
        <>
          <MailMeta>
            <dt>From</dt>
            <dd>{data.fromName ? `${data.fromName} <${data.from}>` : data.from || "—"}</dd>
            <dt>To</dt>
            <dd>{joined(data.to) || "—"}</dd>
            {data.cc?.length ? (
              <>
                <dt>Cc</dt>
                <dd>{joined(data.cc)}</dd>
              </>
            ) : null}
            <dt>Sent</dt>
            <dd>{formatSentAt(data.sentAt || item.sentAt) || "—"}</dd>
          </MailMeta>
          <MailBody>{data.text || "(This email has no text.)"}</MailBody>
          {data.earlier ? (
            <>
              <MailLinks>
                <LinkButton type="button" onClick={() => setShowEarlier((v) => !v)}>
                  {showEarlier
                    ? "Hide earlier messages in this thread"
                    : "Show earlier messages in this thread"}
                </LinkButton>
              </MailLinks>
              {showEarlier ? <MailBody $earlier>{data.earlier}</MailBody> : null}
            </>
          ) : null}
          {data.attachments?.length ? (
            <AttachmentList aria-label="Attachments">
              {data.attachments.map((att) => (
                <li key={att.index}>
                  {isEmailFile(att.name) ? (
                    <a
                      href={boardAppealAttachmentUrl(reviewId, item.id, att.index)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Paperclip size={12} aria-hidden="true" /> {att.name}
                    </a>
                  ) : (
                    <button type="button" onClick={() => onOpenAttachment(item, att)}>
                      <Paperclip size={12} aria-hidden="true" /> {att.name}
                    </button>
                  )}
                </li>
              ))}
            </AttachmentList>
          ) : null}
        </>
      ) : null}
      <MailLinks>
        {children}
        <LinkButton type="button" onClick={() => onOpen(item.documentId, item.documentName)}>
          Open in the middle
        </LinkButton>
      </MailLinks>
    </MailOpen>
  );
};

OpenEmail.propTypes = {
  reviewId: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  email: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  onOpenAttachment: PropTypes.func.isRequired,
  children: PropTypes.node,
};

/**
 * The emails about this referral, filed from Matthew's mailbox, newest
 * first. Clicking one opens it in place, in full; clicking again closes
 * it. An open email can be answered from here, and its attachments or
 * the formatted original shown in the middle viewer.
 */
const AppealCorrespondence = ({
  reviewId,
  items,
  loading,
  error,
  activeId,
  onOpen,
  onOpenAttachment,
  onReply,
}) => {
  const [openId, setOpenId] = useState(null);
  const [emails, setEmails] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const requested = useRef(new Set());

  useEffect(() => {
    setOpenId(null);
    setEmails({});
    setReplyingId(null);
    requested.current = new Set();
  }, [reviewId]);

  const toggle = (item) => {
    if (openId === item.id) {
      setOpenId(null);
      return;
    }
    setOpenId(item.id);
    if (requested.current.has(item.id)) return;
    requested.current.add(item.id);
    setEmails((all) => ({ ...all, [item.id]: { loading: true } }));
    fetchBoardAppealEmailAPI(reviewId, item.id)
      .then(({ data }) => setEmails((all) => ({ ...all, [item.id]: { data } })))
      .catch((err) => {
        requested.current.delete(item.id);
        setEmails((all) => ({ ...all, [item.id]: { error: err?.message || "Could not open that email" } }));
      });
  };

  const send = async (item, reply) => {
    await onReply(item, reply);
    setReplyingId(null);
  };

  return (
    <>
      <MailHeader>
        <MailTitle>Correspondence</MailTitle>
        {items.length ? <Muted style={{ margin: 0 }}>{items.length}</Muted> : null}
      </MailHeader>
      {error ? <Banner role="alert">{error}</Banner> : null}
      <MailScroll aria-label="Emails about this client">
        {loading && !items.length ? <Muted>Loading the emails…</Muted> : null}
        {!loading && !error && !items.length ? (
          <Muted>No emails filed for this client yet.</Muted>
        ) : null}
        {items.map((item) => {
          const decision = (item.matchedBy || []).includes(BOARD_DECISION_MATCH);
          const isOpen = openId === item.id;
          return (
            <div key={item.id}>
              <MailRow
                type="button"
                $active={isOpen || item.documentId === activeId}
                $open={isOpen}
                aria-expanded={isOpen}
                onClick={() => toggle(item)}
              >
                <RowTop>
                  <MailWho>{who(item)}</MailWho>
                  <MailWhen>{formatSentAt(item.sentAt)}</MailWhen>
                </RowTop>
                <MailSubject>{item.subject || "(no subject)"}</MailSubject>
                {item.preview && !isOpen ? <MailPreview>{item.preview}</MailPreview> : null}
                {(item.attachments?.length && !isOpen) || decision ? (
                  <RowTop>
                    <MailAttachments title={(item.attachments || []).join(", ")}>
                      {item.attachments?.length && !isOpen ? (
                        <>
                          <Paperclip size={12} aria-label="Attachments" />{" "}
                          {item.attachments.join(", ")}
                        </>
                      ) : null}
                    </MailAttachments>
                    {decision ? <Chip $tone="ready">Board decision attached</Chip> : null}
                  </RowTop>
                ) : null}
              </MailRow>
              {isOpen ? (
                <OpenEmail
                  reviewId={reviewId}
                  item={item}
                  email={emails[item.id] || { loading: true }}
                  onOpen={onOpen}
                  onOpenAttachment={onOpenAttachment}
                >
                  {item.canReply && replyingId !== item.id ? (
                    <ReplyButton
                      type="button"
                      style={{ margin: 0 }}
                      onClick={() => setReplyingId(item.id)}
                    >
                      <ArrowBendUpLeft size={14} aria-hidden="true" /> Reply
                    </ReplyButton>
                  ) : null}
                </OpenEmail>
              ) : null}
              {isOpen && replyingId === item.id ? (
                <ReplyComposer
                  item={item}
                  onSend={send}
                  onCancel={() => setReplyingId(null)}
                />
              ) : null}
            </div>
          );
        })}
      </MailScroll>
    </>
  );
};

AppealCorrespondence.propTypes = {
  reviewId: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  activeId: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onOpenAttachment: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

export default AppealCorrespondence;
